import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { adminKeys, DELETION_STATUS_LABEL, fetchDeletionStatus } from '../../lib/admin';
import { ApiError } from '../../lib/api';
import { errorText } from '../../lib/copy';
import { PoweredBy } from '../../ui/PoweredBy';
import { Wordmark } from '../../ui/Wordmark';
import styles from './DataDeletionPage.module.css';

const CODE = /^[A-Z0-9]{16,40}$/;
const TIME_ZONE = 'America/El_Salvador';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-SV', { timeZone: TIME_ZONE, day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
}

function Status({ code }: { code: string }) {
  const status = useQuery({ queryKey: adminKeys.deletionStatus(code), queryFn: () => fetchDeletionStatus(code), retry: false });
  if (status.isPending) return <p className={styles.muted}>Consultando la solicitud…</p>;
  if (status.isError) {
    const missing = status.error instanceof ApiError && status.error.status === 404;
    return <p className={styles.alert} role="alert">{missing ? 'No encontramos una solicitud con ese código. Revísalo e inténtalo de nuevo.' : errorText(status.error)}</p>;
  }
  const view = status.data;
  return (
    <section className={styles.card} aria-labelledby="estado">
      <h2 id="estado" className={styles.cardTitle}>Solicitud {view.confirmationCode}</h2>
      <dl className={styles.facts}>
        <div><dt>Estado</dt><dd><span className={styles.status} data-status={view.status}>{DELETION_STATUS_LABEL[view.status]}</span></dd></div>
        <div><dt>Recibida</dt><dd>{formatDate(view.receivedAt)}</dd></div>
        <div><dt>Última actualización</dt><dd>{formatDate(view.updatedAt)}</dd></div>
      </dl>
      {view.publicNote && <p className={styles.note}>{view.publicNote}</p>}
      {view.status === 'IN_PROGRESS' && <p className={styles.muted}>Ya se borraron los permisos que diste a la app Hilo. Una persona responsable revisa el resto de la solicitud.</p>}
    </section>
  );
}

/** Página pública: instrucciones de eliminación de datos (Meta) y seguimiento por código de confirmación. */
export function DataDeletionPage() {
  const [params] = useSearchParams();
  const raw = params.get('codigo')?.trim().toUpperCase() ?? '';
  const code = CODE.test(raw) ? raw : null;

  return (
    <div className={styles.page}>
      <main className={styles.column}>
        <Wordmark size="lg" />
        <h1 className={styles.title}>Eliminación de datos</h1>
        {raw && !code && <p className={styles.alert} role="alert">El código no tiene el formato correcto.</p>}
        {code && <Status code={code} />}
        <section className={styles.section} aria-labelledby="que-guarda">
          <h2 id="que-guarda" className={styles.sectionTitle}>Qué guarda Hilo cuando conectas una página</h2>
          <p>
            Hilo es la bandeja de atención de Corp Salex. Si autorizaste la app Hilo con Facebook para conectar una página, Hilo guarda
            cifrado el permiso de esa página, tu identificador de la app, qué permisos concediste y cuándo. Lo usa solo para recibir y
            responder los mensajes de esa página y de su cuenta de Instagram vinculada.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="como-pedir">
          <h2 id="como-pedir" className={styles.sectionTitle}>Cómo pedir que se borren</h2>
          <ol className={styles.steps}>
            <li>En Facebook, entra en la configuración y busca <strong>Apps y sitios web</strong>.</li>
            <li>Elige <strong>Hilo</strong> y pulsa <strong>Eliminar</strong>.</li>
            <li>Después de quitarla, pide la eliminación de tus datos y envía la solicitud.</li>
          </ol>
          <p>
            Meta avisa a Hilo, que borra en ese momento los permisos guardados y los datos de la autorización, y te da un código de
            confirmación. Para seguir la solicitud y no volver a usar ese permiso, Hilo conserva solo tu identificador de la app y la fecha
            del aviso. Con el código puedes volver a esta página para ver el estado. Si Hilo tiene que conservar algo más, lo explicará aquí.
          </p>
        </section>
        <section className={styles.section} lang="en" aria-labelledby="in-english">
          <h2 id="in-english" className={styles.sectionTitle}>In English</h2>
          <p>
            To delete the data the Hilo app keeps when you connect a Facebook Page, remove the Hilo app in Facebook (settings, Apps and websites, Hilo,
            Remove) and then send the data deletion request. Hilo erases the stored page permissions and the
            authorization data right away, keeps only your app-scoped ID and the date of the request to track it and never reuse that
            permission, and gives you a confirmation code to follow the request on this page.
          </p>
        </section>
        <div className={styles.foot}><PoweredBy /></div>
      </main>
    </div>
  );
}
